'use client';

import { createPost } from '@/actions/forum';
import { Button } from '@ui/primitives';
import { Alert } from '@ui/composites';
import { useTranslation } from '@/lib';
import { event } from '@/lib/analytics/gtag';
import { hasPermission, UserRole } from '@/lib/auth/permissions';
import { UserProfile } from '@/types/users';
import { useActionState, useEffect, useState } from 'react';

interface CreatePostFormProps {
    profile: UserProfile | null;
    topicId: string;
}

export default function CreatePostForm({ profile, topicId }: CreatePostFormProps) {
    const { t, ts } = useTranslation();
    const [state, formAction, isPending] = useActionState(createPost, undefined);
    const [content, setContent] = useState('');

    useEffect(() => {
        if (state && !state.isError) {
            event({ action: 'create_post', category: 'forum', label: String(topicId) });
            setContent('');
        }
    }, [state]);

    if (!profile || !hasPermission(profile.role, UserRole.MEMBER)) {
        return null;
    }

    return (
        <section className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('forum.posts_reply_to_topic')}
            </h2>
            
            <form action={formAction}>
                <input type="hidden" name="topic_id" value={topicId} />
                
                <div className="space-y-4">
                    {state?.message && (
                        <Alert 
                            variant={state.isError ? 'error' : 'success'}
                        >
                            {state.message}
                        </Alert>
                    )}

                    <div>
                        <textarea
                            id="content"
                            name="content"
                            placeholder={ts('forms.content_placeholder')}
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            required
                            disabled={isPending}
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                        />
                    </div>

                    <Button type="submit" disabled={isPending}>
                        {isPending ? t('common.loading') : t('actions.submit')}
                    </Button>
                </div>
            </form>
        </section>
    );
}
